import { db, storage } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { uploadOptimizedImage } from "@/lib/uploadImage";

const SITE_CONTENT_COLLECTION = "siteContent";

/** Fetches a page's CMS doc. Returns null if the admin has never saved
 * anything for this page yet — callers fall back to defaultContent. */
export async function getSiteContent(pageKey) {
  const snap = await getDoc(doc(db, SITE_CONTENT_COLLECTION, pageKey));
  return snap.exists() ? snap.data() : null;
}

/** Saves the working draft without touching what's live. */
export async function saveDraft(pageKey, blocks) {
  await setDoc(
    doc(db, SITE_CONTENT_COLLECTION, pageKey),
    {
      pageKey,
      draft: { blocks },
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

/** Publishes the current draft — copies it into `published` so the public
 * site picks it up. Takes the draft blocks directly (rather than re-reading
 * them) so "Save Draft" + "Publish" from the same editor session always
 * publish exactly what's on screen, no race with a stale read.
 * @param {string} pageKey
 * @param {any[]} blocks
 * @param {string} publishedBy - uid of the admin publishing
 */
export async function publish(pageKey, blocks, publishedBy) {
  await setDoc(
    doc(db, SITE_CONTENT_COLLECTION, pageKey),
    {
      pageKey,
      draft: { blocks },
      published: { blocks },
      updatedAt: serverTimestamp(),
      publishedAt: serverTimestamp(),
      publishedBy,
    },
    { merge: true },
  );
}

/** Uploads an image used inside a page's block content (hero background,
 * about photo, etc.) — mirrors uploadCourseImage's storage-path pattern.
 * Not tied to a specific block/field id since a page can hold several
 * image fields across several blocks. */
export async function uploadSiteContentImage(pageKey, file) {
  const storageRef = ref(
    storage,
    `siteContent/${pageKey}/${Date.now()}_${file.name}`,
  );
  const snap = await uploadOptimizedImage(storageRef, file);
  return getDownloadURL(snap.ref);
}
